import '@/styles/tokens.css';
import '@/styles/global.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { App } from '@/App';
import { API_BASE_URL, MOCK_MODE, PARTIAL_MOCK } from '@/config/env';
import { watchFirebaseSession } from '@/features/auth/firebaseSession';
import { restoreBrandColors } from '@/features/organization/brandTheme';
import { configureSession, getAuthToken, signOut } from '@/features/auth/session';
import { configureFirestoreStream } from '@/hooks/useFirestoreStream';
import i18n from '@/i18n';
import { configureApiClient } from '@/lib/apiClient';

configureApiClient({
  getAuthToken,
  getQueryDefaults: () => ({ language: i18n.language }),
  onUnauthorized: () => signOut('expired'),
});

configureFirestoreStream({ onPermissionDenied: () => signOut('denied') });

restoreBrandColors();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

configureSession({ onSessionEnd: () => queryClient.clear() });

async function start() {
  await watchFirebaseSession();

  if (MOCK_MODE || PARTIAL_MOCK) {
    const { worker } = await import('@/mocks/browser');
    await worker.start({
      // Full mock mode means no request should ever reach the backend, so a
      // silent bypass hides the bug. Warn on API calls only — static assets and
      // fonts are unhandled by design. Partial mode bypasses on purpose.
      onUnhandledRequest(request, print) {
        if (!PARTIAL_MOCK && API_BASE_URL && request.url.startsWith(API_BASE_URL)) {
          print.warning();
        }
      },
    });
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}

void start();
